import { Injectable } from '@angular/core';
import { TransferState, makeStateKey } from '@angular/platform-browser';

import { Observable, throwError, of } from 'rxjs';
import { tap, map, shareReplay, catchError } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';

import { LocationContactResponse, LocationContactsResponse } from '..//models/rest/contact/lotContactResponses';

import {
  GetLocationContactsRequest,
  GetOrDeleteLocationContactRequest,
  InsertOrUpdateLocationContactRequest,
  ReorderLocationContactsRequest,
} from '../models/rest/contact/lotContactRequests';
import { BaseResponse } from '../models/rest/baseResponse';
import { AppStateService } from './app-state.service';
import { AuditActionType, AuditHelper } from './audit.helper';

export class LotModel {
  Id?: number;
  IsActive?: boolean;
  IsDeleted?: boolean;
  LotNumber?: number | string;
  LotName?: string;

  IsStaticHtmlExist: boolean;

  Street?: string;
  City?: string;
  UState?: string;
  Zip?: string;
  Lat?: number;
  Lng?: number;

  GeneralInfo?: string;
  Contacts: any;
  Rates?: string;

  fkManager?: number;
  fkOwner?: number;
  fkLocationType: number;

  GateInstructions: any;
  Equipment: string;

  FacilityCode: string;
  RevenueClientSettings: any;
  RevenueControlEnabled: boolean;
  fkRevenueControlManufacturer: number;
  TimeZone: string;
  RevenueControlBehindGateway: boolean;
  RevenueControlGatewayId: string;
  RevenueControlReserveGatewayId: string;
  ParkonectId: number;
  ParkonectUsername: string;
  ParkonectPassword: string;
  fkSalesRegion?: number;
  fkRuleSet: number;
  ConfluenceId: string;
  DefaultView: string;
  IntercomManufacturer: any = null;
  integrationProvider: any = null;
  DVRManufacturer: string;
  DVRWebIP: string;
  DVRWebPort: number;
  DVRUserName: string;
  DVRPwd: string;
  AxxonDomainId: number;
  ImmediatelyContact?: string;
  ThinxId?: string;
  VoicePlatformAccountId?: string;
  OcraLocationId?: string;
  ParkchirpLocationId?: string;
  NoodoeLocationId?: string;
  OcraEnabled: boolean;
  ParkchirpEnabled: boolean;
  NoodoeEnabled: boolean;
  OcraDefaultTimeFrame?: string;
  ParkchirpDefaultTimeFrame?: string;

  InexLPREnabled: boolean;
  InexLPRUrl?: string;
  InexLPRUsername?: string;
  InexLPRPassword?: string;
  InexLPRLocationId?: string;
  InexLPRDefaultTimeFrame?: string;

  MitteLocationId?: string;
  MitteDefaultTimeFrame?: string;
  MitteEnabled: boolean;

  Intercom_PhoneNumber?: string;
  IsVoiceBotEnabled: boolean;
  fkVoiceBot?: number;
  DvrSerialNumber?: string;
  DvrConnectionType?: number;
  AnalogGateVendCode: boolean;
  VideoRecordingEnabled: boolean;
  DisplayActiveIntegrations: boolean;
}

export class LocationIFrameTab {
  Id: number;
  Order: number;
  Enabled: boolean;
  Title: string;
  Url: string;
  BasicAuthEnabled: boolean;
  Username: string;
  Password: string;
  fkLot: number;
}

export interface ILocationType {
  Id: number;
  Name: string;
}

interface IGetLocationTagsResponse extends BaseResponse {
  Tags: Array<string>;
}

interface IGetLocationIFrameTabsResponse extends BaseResponse {
  IFrameTabs: Array<LocationIFrameTab>;
}

interface IGetLocationIFrameTabResponse extends BaseResponse {
  IFrameTab: LocationIFrameTab;
}

interface IInsertOrUpdateLocationIFrameTabResponse extends BaseResponse {
  IFrameTab: LocationIFrameTab;
}

interface IGetLocationTypesResponse extends BaseResponse {
  Types: Array<ILocationType>;
}

export interface IGetActiveManufacturersByRuleSetId extends BaseResponse {
  ManufacturerIds: Array<number>;
  ActiveIntegrations: Array<string>;
}

@Injectable({ providedIn: 'root' })
export class LocationsService {
  private locationsCache$: Observable<any> | null = null;
  private readonly TRANSFER_STATE_KEY = makeStateKey<string>('locations-cache');

  constructor(
    private _httpClient: HttpClient,
    private appStateService: AppStateService,
    private transferState: TransferState,
  ) {
    this.appStateService.isAuthenticated$.subscribe((res) => {
      if (!res) {
        this.resetCache();
      }
    });
  }

  getAll(): Observable<any> {
    if (this.locationsCache$) {
      return this.locationsCache$;
    }

    this.locationsCache$ = this._httpClient.post<any>('Lot/LotDirectory', null).pipe(
      tap((response) => {
        if (response && response.Success) {
          this.saveInCache(response);
        } else {
          this.locationsCache$ = null;
        }
      }),
      catchError((error) => {
        this.locationsCache$ = null;
        return throwError(error);
      }),
      shareReplay(1)
    );

    return this.locationsCache$;
  }

  /* WebAgent SSR version cache method */
  getAllSRRCacheVersion(): Observable<any> {
    let locations = this.getFromCache();
    if (!!locations) {
      return of(locations);
    }

    return this._httpClient.post<any>('Lot/LotDirectory', null).pipe(
      tap((response) => {
        if (response && response.Success) {
          this.saveInCache(response);
          this.locationsCache$ = of(response).pipe(shareReplay(1));
        } else {
          this.locationsCache$ = null;
        }
      }),
      catchError((error) => {
        this.locationsCache$ = null;
        return throwError(error);
      })
    );
  }

  updateLotOwner(request) {
    return this.update('Owner', request, 'Location owner');
  }

  updateLotManager(request) {
    return this.update('Manager', request, 'Location manager');
  }

  update(field, UpdateLotRequest, subsection: string): Observable<any> {
    if (arguments.length < 2) {
      console.error('missing fields to update');
      return throwError('missing fields to update');
    }

    return this._httpClient
      .put<any>('Lot/UpdateLot' + field, UpdateLotRequest, {
        headers: AuditHelper.createAuditHeader({ entityId: UpdateLotRequest.LotId, section: 'Contact List', subsection }),
      })
      .pipe(tap((response) => this.updateCacheLots(response.Lot)));
  }

  insertOrUpdateLotField(lotId, field, value, method: AuditActionType, section, subsection = null): Observable<any> {
    if (arguments.length < 3) {
      console.error('missing fields to update');
      return throwError('missing fields to update');
    }

    const options = {
      body: { FieldValue: value },
      headers: AuditHelper.createAuditHeader({ entityId: lotId, section, subsection }),
    };

    return this._httpClient
      .request<any>(method, `Lot/${lotId}/${field}`, options)
      .pipe(tap((response) => this.updateCacheLots(response.Lot)));
  }

  updateLotConfiguration(UpdateLotRequest, section, subsection = '', action = ''): Observable<any> {
    return this._httpClient
      .put<any>('Lot/UpdateLotConfiguration', UpdateLotRequest, {
        headers: AuditHelper.createAuditHeader({ entityId: UpdateLotRequest.LotModel.Lot.Id, section, subsection, action }),
      })
      .pipe(tap((response) => this.updateCacheLots(response.Lot)));
  }

  updateCacheLots(lot) {
    let locations = this.getFromCache();
    if (locations) {
      let location = locations.Data.find((x) => x.Id === lot.Id);

      if (location) {
        Object.assign(location, lot);
        this.saveInCache(locations);
      }
    }

    this.locationsCache$ = null;
  }

  insert(lotModel): Observable<any> {
    return this._httpClient.post<any>('Lot/InsertLotConfiguration', { LotModel: { Lot: lotModel } }).pipe(
      tap((response) => {
        let locations = this.getFromCache();
        if (locations) {
          locations.Data.push(response.Lot);
          this.saveInCache(locations);
        }
        this.locationsCache$ = null;
      }),
    );
  }

  restoreLot(LotId): Observable<any> {
    return this._httpClient.post<any>(
      'Lot/UnDelete',
      { Id: LotId },
      {
        headers: AuditHelper.createAuditHeader({ entityId: LotId, section: 'Location', subsection: '', action: 'Location - Restored' }),
      },
    ).pipe(
      tap(() => this.resetCache())
    );
  }

  delete(LotId): Observable<any> {
    return this._httpClient
      .post<any>(
        'Lot/Delete',
        { Id: LotId },
        {
          headers: AuditHelper.createAuditHeader({ entityId: LotId, section: 'Location', subsection: '', action: 'Location - Archived' }),
        },
      )
      .pipe(
        tap(() => {
          let locations = this.getFromCache();
          if (locations) {
            locations.Data = locations.Data.filter((x) => x.Id !== LotId);
            this.saveInCache(locations);
          }
          this.locationsCache$ = null;
        }),
      );
  }

  setActive(lotId, active: boolean): Observable<any> {
    return this._httpClient
      .post<any>('Lot/SetActive', { Id: lotId, IsActive: active })
      .pipe(
        tap((response) => {
          this.updateCacheLots(response.Lot);
        }),
      );
  }

  getByLot(GetLotRequest): Observable<{ Lot: LotModel }> {
    return this._httpClient.post<any>('Lot/GetLotManagementByLotNumber', GetLotRequest);
  }

  getBy(id: number, includeDeleted = false
        , IncludeRegisteredDevices = false): Observable<{ Lot: LotModel; Lanes: Array<any>; LotIssues: Array<any> }> {
    let url = `Lot/GetLocationManagement`;
    return this._httpClient.post<any>(url, { id, includeDeleted, IncludeRegisteredDevices });
  }

  getLocationContacts(request: GetLocationContactsRequest): Observable<LocationContactsResponse> {
    return this._httpClient.get<LocationContactsResponse>(`Lot/${request.LotId}/Contacts`);
  }

  getLocationContact(request: GetOrDeleteLocationContactRequest): Observable<LocationContactResponse> {
    return this._httpClient.get<LocationContactResponse>(`Lot/${request.LotId}/Contacts/${request.ContactId}`);
  }

  reorderLocationContacts(request: ReorderLocationContactsRequest): Observable<BaseResponse> {
    return this._httpClient.post<BaseResponse>(`Lot/${request.LotId}/ReorderContacts`, request);
  }

  updateLocationContact(request: InsertOrUpdateLocationContactRequest): Observable<LocationContactResponse> {
    return this.insertOrUpdateLocationContact(request, AuditActionType.UPDATE);
  }

  createLocationContact(request: InsertOrUpdateLocationContactRequest): Observable<LocationContactResponse> {
    return this.insertOrUpdateLocationContact(request, AuditActionType.CREATE);
  }

  private insertOrUpdateLocationContact(
    request: InsertOrUpdateLocationContactRequest,
    method: AuditActionType,
  ): Observable<LocationContactResponse> {
    const options = {
      body: request,
      headers: AuditHelper.createAuditHeader({ entityId: request.LocationContact.fkLot, section: 'Contacts' }),
    };
    const url = `Lot/${request.LocationContact.fkLot}/Contacts/${request.LocationContact.fkContact}`;
    return this._httpClient.request<LocationContactResponse>(method, url, options);
  }

  deleteLocationContact(request: GetOrDeleteLocationContactRequest): Observable<LocationContactResponse> {
    return this._httpClient.delete<LocationContactResponse>(`Lot/${request.LotId}/Contacts/${request.ContactId}`, {
      headers: AuditHelper.createAuditHeader({
        entityId: request.LotId,
        section: 'Contacts',
      }),
    });
  }

  getLocationTags(lotId: number): Observable<Array<string>> {
    return this._httpClient.get<IGetLocationTagsResponse>(`Lot/${lotId}/Tags`).pipe(map((x) => x.Tags));
  }

  saveLocationTags(lotId: number, tags: Array<string>): Observable<BaseResponse> {
    return this._httpClient.post<BaseResponse>(`Lot/${lotId}/Tags`, {
      tags: tags,
    });
  }

  getLocationIFrameTabs(lotId: number): Observable<IGetLocationIFrameTabsResponse> {
    return this._httpClient.get<IGetLocationIFrameTabsResponse>(`Lot/${lotId}/IFrameTabs`);
  }

  getLocationIFrameTab(id: number): Observable<IGetLocationIFrameTabResponse> {
    return this._httpClient.get<IGetLocationIFrameTabResponse>(`Lot/IFrameTabs/${id}`);
  }

  reorderLocationIFrameTabs(lotId: number, tabs: Array<LocationIFrameTab>): Observable<BaseResponse> {
    return this._httpClient.post<BaseResponse>(`Lot/${lotId}/IFrameTabs/Reorder`, { IFrameTabs: tabs });
  }

  insertLocationIFrameTab(tab: LocationIFrameTab): Observable<IInsertOrUpdateLocationIFrameTabResponse> {
    return this.insertOrUpdateIFrameTab(tab, AuditActionType.CREATE);
  }

  updateLocationIFrameTab(tab: LocationIFrameTab): Observable<IInsertOrUpdateLocationIFrameTabResponse> {
    return this.insertOrUpdateIFrameTab(tab, AuditActionType.UPDATE);
  }

  private insertOrUpdateIFrameTab(tab: LocationIFrameTab, method: AuditActionType): Observable<IInsertOrUpdateLocationIFrameTabResponse> {
    const options = {
      body: { IFrameTab: tab },
      headers: AuditHelper.createAuditHeader({
        entityId: tab.fkLot,
        section: 'Additional Settings',
        subsection: 'Screen Tabs',
      }),
    };

    return this._httpClient.request<IInsertOrUpdateLocationIFrameTabResponse>(method, `Lot/${tab.fkLot}/IFrameTabs`, options);
  }

  deleteLocationIFrameTab(lotId: number, id: number): Observable<LocationContactResponse> {
    return this._httpClient.delete<LocationContactResponse>(`Lot/IFrameTabs/${id}`, {
      headers: AuditHelper.createAuditHeader({
        entityId: lotId,
        section: 'Additional Settings',
        subsection: 'Screen Tabs',
      }),
    });
  }

  getLocationTypes(): Observable<Array<ILocationType>> {
    return this._httpClient.get<IGetLocationTypesResponse>(`Lot/Types`).pipe(map((x) => x.Types));
  }

  getActiveManufacturersByRuleSetId(ruleSetId: number): Observable<IGetActiveManufacturersByRuleSetId> {
    return this._httpClient
      .get<IGetActiveManufacturersByRuleSetId>(`RuleSet/${ruleSetId}/ActiveManufacturers`);
  }

  private getFromCache(): any {
    return this.transferState.get(this.TRANSFER_STATE_KEY, null);
  }

  private saveInCache(value): any {
    return this.transferState.set(this.TRANSFER_STATE_KEY, value);
  }

  private resetCache() {
    this.locationsCache$ = null;
    this.transferState.remove(this.TRANSFER_STATE_KEY);
  }
}
